import React, { useState, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { STORAGE_KEYS } from '@constants/index';
import { NetworkOrb } from '@aibuild/ui';
import PromptComposer from '@components/PromptComposer';
import {
  Container,
  HeroIcon,
  Title,
  InputOuterWrapper,
  ChipsSection,
  ChipsList,
  PromptChip,
} from './TaskPrompt.styled';
import RunnerPicker from '@components/Chat/RunnerPicker';
import ApprovalModePicker from '@components/Chat/ApprovalModePicker';
import LlmModelPicker from '@components/Chat/LlmModelPicker';
import { type ApprovalMode, APPROVAL_MODE_DEFAULT, getStoredApprovalMode, setStoredApprovalMode } from '@utils/approvalMode';
import { getStoredLlmModel, setStoredLlmModel } from '@utils/llmModel';
import { setPendingUploadFiles } from '@utils/pendingFiles';
import { listLocalRunners } from '@api/localRunners';
import type { LocalRunner } from '@api/localRunners';
import useAuth from '@contexts/useAuth';
import { useChatInputLimits } from '@hooks/useChatInputLimits';
import { useFileAttachments } from '@hooks/useFileAttachments';
import { EXAMPLE_PROMPTS, type ExamplePrompt } from './examplePrompts';

const CHAT_INPUT_PARAM = 'chat-interface__input';

const TaskPrompt: React.FC = () => {
  const intl = useIntl();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [seededInputValue, setSeededInputValue] = useState(() => {
    // Already-logged-in path: param is on the current URL
    const fromUrl = searchParams.get(CHAT_INPUT_PARAM);
    if (fromUrl) {
      // Clear any stale stored value so it doesn't surface on the next visit
      localStorage.removeItem(STORAGE_KEYS.PENDING_CHAT_INPUT);
      return fromUrl;
    }
    // Post-auth path: param was stashed in localStorage by the Landing page
    const fromStorage = localStorage.getItem(STORAGE_KEYS.PENDING_CHAT_INPUT);
    if (fromStorage) {
      localStorage.removeItem(STORAGE_KEYS.PENDING_CHAT_INPUT);
      return fromStorage;
    }
    return '';
  });
  const { maxAttachmentsPerMessage, maxMessageLengthChars } = useChatInputLimits();
  const attachments = useFileAttachments({ maxFiles: maxAttachmentsPerMessage });
  const { pendingFiles, clearFiles } = attachments;
  const [runnerType, setRunnerType] = useState<string>('cloud');
  const [localRunners, setLocalRunners] = useState<LocalRunner[]>([]);
  const [approvalMode, setApprovalMode] = useState<ApprovalMode>(
    () => getStoredApprovalMode(null) ?? APPROVAL_MODE_DEFAULT,
  );
  const [llmModel, setLlmModel] = useState<string>(() => getStoredLlmModel(null));

  useEffect(() => {
    let cancelled = false;
    listLocalRunners()
      .then((runners) => {
        if (!cancelled) setLocalRunners(runners);
      })
      .catch(() => {
        // No local runners available
      });
    return () => { cancelled = true; };
  }, []);

  // Derived, not corrected in an effect, so it is already clamped on the render
  // where the fetched limit replaces the fallback.
  const inputValue = seededInputValue.slice(0, maxMessageLengthChars);

  // The single exit from this page: a seeded or example prompt never passes
  // through the textarea's own maxLength, so the limit is applied here instead.
  const startConversation = useCallback(
    (message: string, extraState: Record<string, unknown> = {}) => {
      navigate('/conversation/new', {
        state: {
          initialMessage: message.slice(0, maxMessageLengthChars),
          runnerType: runnerType || undefined,
          approvalMode,
          ...extraState,
        },
      });
    },
    [navigate, runnerType, approvalMode, maxMessageLengthChars],
  );

  const handleSubmit = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed && pendingFiles.length === 0) return;

    const filesToSend = pendingFiles.length > 0 ? pendingFiles : undefined;

    setSeededInputValue('');
    clearFiles();

    setPendingUploadFiles(filesToSend);
    startConversation(trimmed || 'See attached files', { hasFiles: !!filesToSend });
  }, [inputValue, pendingFiles, clearFiles, startConversation]);

  const sendPrompt = useCallback(
    (example: ExamplePrompt) => {
      const promptText = intl.formatMessage({
        id: example.promptId ?? example.id,
        defaultMessage: example.promptDefaultMessage ?? example.defaultMessage,
      });
      setPendingUploadFiles(undefined);
      startConversation(promptText, {
        attachmentUrls: example.attachments,
        hasFiles: !!example.attachments?.length,
      });
    },
    [intl, startConversation],
  );

  const handlePickRunner = useCallback((value: string) => {
    setRunnerType(value);
  }, []);

  const handlePickApprovalMode = useCallback((mode: ApprovalMode) => {
    setApprovalMode(mode);
    setStoredApprovalMode(null, mode);
  }, []);

  const handlePickLlmModel = useCallback((model: string) => {
    setLlmModel(model);
    setStoredLlmModel(null, model);
  }, []);

  const firstName = user?.profileData?.firstName?.trim();
  const heroTitle = firstName
    ? intl.formatMessage(
        { id: 'taskPrompt.heroTitle.withName', defaultMessage: 'What can I do for you, {firstName}?' },
        { firstName },
      )
    : intl.formatMessage({ id: 'taskPrompt.heroTitle', defaultMessage: 'What can I do for you?' });

  return (
    <Container>
      <HeroIcon>
        <NetworkOrb size={170} />
      </HeroIcon>

      <Title>{heroTitle}</Title>

      <InputOuterWrapper>
        <PromptComposer
          value={inputValue}
          onChange={setSeededInputValue}
          onSubmit={handleSubmit}
          canSubmit={!!inputValue.trim() || pendingFiles.length > 0}
          attachments={attachments}
          maxLength={maxMessageLengthChars}
          placeholder={intl.formatMessage({ id: 'taskPrompt.inputPlaceholder', defaultMessage: 'Assign a task or ask anything...' })}
          attachLabel={intl.formatMessage({ id: 'taskPrompt.uploadButton.title', defaultMessage: 'Upload files or images' })}
          sendLabel={intl.formatMessage({ id: 'taskPrompt.sendButton.title', defaultMessage: 'Send message' })}
          autoFocus
          footer={
            <>
              <RunnerPicker
                runnerType={runnerType}
                localRunners={localRunners}
                onPick={handlePickRunner}
                menuDirection="down"
              />
              <LlmModelPicker
                llmModel={llmModel}
                onPick={handlePickLlmModel}
                menuDirection="down"
              />
              {runnerType !== 'cloud' && (
                <ApprovalModePicker
                  approvalMode={approvalMode}
                  onPick={handlePickApprovalMode}
                  menuDirection="down"
                />
              )}
            </>
          }
        />
      </InputOuterWrapper>

      <ChipsSection>
        <ChipsList>
          {EXAMPLE_PROMPTS.map((example) => {
            const label = intl.formatMessage({
              id: example.id,
              defaultMessage: example.defaultMessage,
            });
            return (
              <PromptChip key={example.id} onClick={() => sendPrompt(example)}>
                {label}
              </PromptChip>
            );
          })}
        </ChipsList>
      </ChipsSection>
    </Container>
  );
};

export default TaskPrompt;
